import { getSettings, useSettings } from '@/data/Settings';
import { getPageDataFromId, usePageDataFromId } from '@/data/_PageDataFromId';
import { DEFAULT_LAYOUT } from '@/data/config/DEFAULTS';
import { useEmarsys } from '@/data/context/emarsysCtx';
import { EventsContext } from '@/data/context/events';
import { Cache } from '@/data/types/Cache';
import { ContainerLayout } from '@/data/types/ContainerLayout';
import { Layout } from '@/data/types/Layout';
import { ContentItem } from '@/data/types/LayoutAllSlots';
import { PageDataFromId } from '@/data/types/PageDataFromId';
import { encodeRedirectPath } from '@/data/utils/encodeRedirectPath';
import { filterDynamicPaths } from '@/data/utils/filterDynamicPaths';
import { getNormalizedLayout } from '@/data/utils/getNormalizedLayout';
import { getServerSideRedirectPrefix } from '@/data/utils/getServerSideRedirectPrefix';
import { postPreviewMessage } from '@/data/utils/postPreviewMessage';
import { isEqual } from 'lodash';
import { GetServerSidePropsContext } from 'next';
import { ParsedUrlQuery } from 'querystring';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { EMARSYS_TYPE, EMARSYS_WIDGET_NAME } from 'shared/custom/utils/emarsys';

type ProcessedLayout = {
	value?: PageDataFromId;
	processed: Layout;
	redirect?: string;
	permanent?: boolean;
};
type GetLayoutReturn = Promise<ProcessedLayout>;

const dataMap = (contents: any): Layout =>
	(contents ? [contents] : []).reduce(
		(layout, item) => ({
			...layout,
			tokenExternalValue: item?.tokenExternalValue,
			tokenName: item?.tokenName,
			isProductSKU: item?.isProductSKU,
			...getNormalizedLayout(item),
		}),
		DEFAULT_LAYOUT
	);

export const useLayout = () => {
	const { settings } = useSettings();
	const { data, error, loading } = usePageDataFromId();
	const { state, dispatch } = useEmarsys();
	const emarsysTypeExecuted = state.emarsysWidgetType.executed;
	const { onPageView } = useContext(EventsContext);
	const layoutRef = useRef<ContainerLayout>();
	const { inPreview } = settings;
	// Global object to know if we have additional, regular emarsys widgets and the count
	const [emarsysCarouselWidgets, setEmarsysCarouselWidgets] = useState({
		regular: false,
		additional: false,
		count: 0,
	});
	const getEmarsysWidget = useCallback((widget: ContentItem) => {
		const { widgetRaw } = widget;
		if (widgetRaw?.widgetName === EMARSYS_WIDGET_NAME) {
			if (widgetRaw?.properties?.emarsysProductType === EMARSYS_TYPE.REGULAR) {
				setEmarsysCarouselWidgets((prevState) => ({
					...prevState,
					regular: true,
				}));
			}
			if (widgetRaw?.properties?.emarsysProductType === EMARSYS_TYPE.ADDITIONAL) {
				setEmarsysCarouselWidgets((prevState) => ({
					...prevState,
					additional: true,
				}));
			}
		}
	}, []);
	useEffect(() => {
		if (inPreview && data?.layout && !isEqual(layoutRef.current, data)) {
			postPreviewMessage({
				message: {
					data: { ...data },
					action: 'PREVIEW_LAYOUT_INITIALIZED',
				},
			});
			layoutRef.current = data;
		}
	}, [data, inPreview]);

	useEffect(() => {
		if (data?.page) onPageView(data.page);
	}, [onPageView, data?.page]);

	const layout = useMemo(() => dataMap(data), [data]);
	useEffect(() => {
		if (!emarsysTypeExecuted) {
			layout.slots.first?.forEach(getEmarsysWidget);
			layout.slots.second?.forEach(getEmarsysWidget);
			layout.slots.third?.forEach(getEmarsysWidget);
			layout.slots.fourth?.forEach(getEmarsysWidget);
			layout.slots.fifth?.forEach(getEmarsysWidget);
		}
	}, [layout, emarsysTypeExecuted, getEmarsysWidget]);

	// If we have a emarsys widget execute this dispatch
	useEffect(() => {
		if (
			!emarsysTypeExecuted &&
			(emarsysCarouselWidgets.additional || emarsysCarouselWidgets.regular)
		) {
			dispatch({
				type: 'EMARSYS_WIDGETS_TYPES',
				payload: { ...emarsysCarouselWidgets, executed: true },
			});
		}
	}, [dispatch, emarsysCarouselWidgets, emarsysTypeExecuted]);

	return {
		data,
		layout,
		loading,
		error,
	};
};

export const getLayout = async (
	cache: Cache,
	path: ParsedUrlQuery['path'],
	context: GetServerSidePropsContext
): GetLayoutReturn => {
	const processedPath = filterDynamicPaths(path as string[]);
	const page = await getPageDataFromId(cache, processedPath, context);

	const { storeToken: { urlKeywordName } = {} } = await getSettings(cache, context);
	const redirectPrefix = getServerSideRedirectPrefix({
		contextLocale: context.locale,
		storeUrlKeyword: urlKeywordName,
	});
	const redirect = encodeRedirectPath(page?.page?.redirect ?? '');
	return redirect
		? {
				redirect: `${redirectPrefix}/${redirect}`,
				value: undefined,
				processed: DEFAULT_LAYOUT,
				permanent: page?.page?.permanent ?? false,
		  }
		: {
				value: page,
				processed: dataMap(page),
		  };
};
